import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { env } from "@/lib/env";
import { getToken } from "@/utils/helpers";

export interface UploadResponse {
  success: boolean;
  data: {
    documentId: string;
    message: string;
  };
}

export interface DeletePdf {
  success: boolean;
  data: {
    //
  };
}

export interface UploadRequest {
  file: File;
}

export interface PdfHashResponse {
  success: boolean;
  data: {
    createdAt: string;
    fieldHash: string;
    fieldName: string;
    id: string;
    invoiceDate: string;
    invoiceNumber: string;
    pdfLink: string;
    updatedAt: string;
  }[];
}

export interface SingleInvoiceResponse {
  success: boolean;
  data: {
    id: string;
    createdAt: Date;
    updatedAt: Date;
    date: Date;
    tippingYard: string;
    vehicle: string;
    wasteType: string;
    weight: number;
    rate: number;
    totalAmount: number;
    account: string;
    invoiceId: string;
    docketNumber: string;
    isVerified: boolean;
  };
}

interface WasteRecord {
  id: string;
  createdAt: string;
  updatedAt: string;
  date: string;
  tippingYard: string;
  vehicle: string;
  wasteType: string;
  weight: number;
  rate: number;
  totalAmount: number;
  account: string;
  invoiceId: string;
  docketNumber: string;
  isVerified: boolean;
}

interface Invoice {
  id: string;
  createdAt: string;
  updatedAt: string;
  invoiceNumber: string;
  invoiceDate: string;
  pdfLink: string;
  fieldName: string;
  fieldHash: string;
  isVerified: boolean;
}

interface ResponseData {
  invoice: Invoice;
  wasteRecords: WasteRecord[];
}

interface ApiResponse {
  message: string;
  data: ResponseData;
}

interface InvoiceEdit {
  id: string;
  data: {
    [key: string]: string | number;
  };
}

interface WasteRecordedit {
  id: string;
  data: {
    [key: string]: string | number;
  };
}

export type DataStructureInvoiceEdit = {
  invoice: InvoiceEdit;
  wasterecords: WasteRecordedit[];
};

export const documentParserApi = createApi({
  reducerPath: "documentParserApi",
  baseQuery: fetchBaseQuery({
    baseUrl: env.BACKEND_URL,
  }),
  tagTypes: ["PdfHash", "SingleInvoice"], // Define tag types
  endpoints: (builder) => ({
    uploadSinglePDF: builder.mutation<UploadResponse, UploadRequest>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("pdf", file);

        return {
          url: "/invoices/create",
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        };
      },
      transformResponse: (response: UploadResponse): UploadResponse => response,
      invalidatesTags: ["PdfHash"],
    }),
    deleteSinglePdf: builder.mutation<UploadResponse, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/invoices/${id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        };
      },
      transformResponse: (response: UploadResponse): UploadResponse => response,
      invalidatesTags: ["PdfHash"],
    }),
    getPdfHash: builder.query<PdfHashResponse, void>({
      query: () => ({
        url: `/invoices/all`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      transformResponse: (response: PdfHashResponse): PdfHashResponse =>
        response,
      providesTags: ["PdfHash"],
    }),
    getSingleInvoice: builder.query<ApiResponse, string>({
      query: (id) => ({
        url: `/invoices/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      transformResponse: (response: ApiResponse): ApiResponse => response,
      providesTags: ["SingleInvoice"],
    }),
    updateAllInvoices: builder.mutation<
      UploadResponse,
      DataStructureInvoiceEdit
    >({
      query: (data) => ({
        url: "/invoices/updateall",
        method: "PUT",
        body: data,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      transformResponse: (response: UploadResponse): UploadResponse => response,
      invalidatesTags: ["SingleInvoice"],
    }),
    verifyInvoice: builder.mutation<UploadResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/invoices/verify/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      transformResponse: (response: UploadResponse): UploadResponse => response,

      invalidatesTags: ["SingleInvoice"],
    }),
  }),
});

export const {
  useUploadSinglePDFMutation,
  useDeleteSinglePdfMutation,
  useGetPdfHashQuery,
  useGetSingleInvoiceQuery,
  useUpdateAllInvoicesMutation,
  useVerifyInvoiceMutation,
} = documentParserApi;
